import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, reset } from 'redux-form';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { cloneForm } from 'actions';
import usePrevious from 'hooks/usePrevious';

import AutoCompleteInput from 'components/Common/Inputs/AutoCompleteInput';

const CloneForm = ({ dispatch, title, introText, sourceAccountId, forms, accounts, isCloningForm, cloneFormComplete, onSuccess, customerSupportingDocs }) => {
  const [formId, setFormId] = useState(null);
  const [sourceAccount, setSourceAccount] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [account, setAccount] = useState(null);
  const [accountPartOfAnImyStructure, setAccountPartOfAnImyStructure] = useState(false);
  const [cloneCollectableSupportingDocs, setCloneCollectableSupportingDocs] = useState(null);

  const prevIsCloningForm = usePrevious(isCloningForm);

  const clearForm = () => {
    setFormId(null);
    setSourceAccount(null);
    setCustomer(null);
    setAccount(null);
    setAccountPartOfAnImyStructure(false);
    setCloneCollectableSupportingDocs(null);
  };

  useEffect(() => {
    if (prevIsCloningForm && !isCloningForm && cloneFormComplete) {
      clearForm();
      dispatch(reset('clone-form'));
    }
  }, [isCloningForm, cloneFormComplete]);

  const formDescription = (form) => {
    if (sourceAccountId) {
      return `${ form.formTypeDescription } - ${ moment(form.dateUploaded).format('MMM DD, YYYY') } - ${ form.entityName ?? 'N/A' }`;
    }
    return `${ form.formTypeDescription } - ${ moment(form.dateUploaded).format('MMM DD, YYYY') } - ${ form.accountNumber ?? 'N/A' }`;
  };

  const accountDescription = a => `${ a.accountNumber } - ${ a.accountName }`;

  const onFormSelected = (value) => {
    if (value && forms) {
      setFormId(parseInt(value, 10));
      const formSelected = forms.filter(f => f.id === parseInt(value, 10));
      if (formSelected && formSelected.length > 0) {
        setCustomer({ id: formSelected[0].customerDbId, customerId: formSelected[0].customerId, name: formSelected[0].customerName });
        if (formSelected[0].accountId) {
          setSourceAccount({ id: formSelected[0].accountId, accountNumber: formSelected[0].accountNumber, name: formSelected[0].accountName });
        }
        return;
      }
    }
    setFormId(null);
    setSourceAccount(null);
    setCustomer(null);
    setAccount(null);
    setAccountPartOfAnImyStructure(false);
    setCloneCollectableSupportingDocs(null);
  };

  const onAccountSelected = (accountValue) => {
    setAccount(accountValue);
    if (accountValue.parentAccountId || accountValue.withholdingStatementId) {
      setAccountPartOfAnImyStructure(true);
    }

    if (accountValue.collectableSupportingDocumentTypes !== undefined) {
      const supportingDocumentsForForm = customerSupportingDocs?.filter(f => f.formId === formId);
      if (supportingDocumentsForForm?.length > 0) {
        const filteredDocuments = supportingDocumentsForForm.filter(doc => accountValue.collectableSupportingDocumentTypes.includes(doc.type));
        if (filteredDocuments.length === 0) {
          setCloneCollectableSupportingDocs(false);
        } else {
          setCloneCollectableSupportingDocs(true);
        }
      }
    }
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (formId && customer && account) {
      dispatch(cloneForm(formId, customer.id, sourceAccount?.id ?? sourceAccountId, customer.id, account.id, onSuccess, cloneCollectableSupportingDocs));
    }
  };

  return (<div className="move-clone-form">
    { title && <h2><strong>{ title }</strong></h2> }
    { introText && <p>{ introText }</p> }
    <form onSubmit={ onFormSubmit }>
      <div className="form-group columns">
        <div className="col-1">
          <label htmlFor="clone-form-id" className="panel-body-label">Form</label>
        </div>
        <div className="col-6">
          <Field
            id="clone-form-id"
            name="formId"
            component="select"
            className="form-select"
            required
            value={ formId ? formId.toString() : null }
            onChange={ e => onFormSelected(e.target.value) }
          >
            <option value="">Select form...</option>
            { forms && forms.map(f => (<option key={ f.id } value={ f.id }>{ formDescription(f) }</option>)) }
          </Field>
        </div>
      </div>
      <div className="form-group columns">
        <div className="col-1">
          <label htmlFor="customer-id" className="panel-body-label">Shareholder</label>
        </div>
        <div className="col-6">
          { !customer && <p>Select form...</p> }
          { customer && <p>{ `${ customer.customerId } - ${ customer.name }` }</p> }
        </div>
      </div>
      <div className="form-group columns">
        <div className="col-1">
          <label htmlFor="account-id" className="panel-body-label">Account</label>
        </div>
        <div className="col-6">
          { accounts && accounts.length > 0 && <Field
            id="clone-account-id"
            name="accountId"
            component="select"
            className="form-select"
            required
            value={ account ? account.id.toString() : null }
            onChange={ e => setAccount(e.target.value ? parseInt(e.target.value, 10) : null) }
          >
            <option value="">Select form</option>
            { accounts && accounts.map(a => (<option key={ a.id } value={ a.id }>{ accountDescription(a) }</option>)) }
          </Field> }
          { (!accounts || accounts.length === 0) && <AutoCompleteInput
            id="account-id"
            name="accountId"
            category="account"
            context={ customer ? { type: 'customer', id: parseInt(customer.id, 10), value: '' } : null }
            placeholder="Enter account number..."
            className="form-select"
            required
            disabled={ customer === null }
            value={ account || '' }
            exclude={ sourceAccount ? [`${ sourceAccount.accountNumber } - ${ sourceAccount.name }`] : null }
            onSelectedItem={ (value) => { onAccountSelected(value); } }
            onChange={ (e) => { setAccount(null); setAccountPartOfAnImyStructure(false); setCloneCollectableSupportingDocs(null); } }
          /> }
        </div>
      </div>
      {accountPartOfAnImyStructure
        && <div className="form-group col-12 col-sm-12">
          <div className="col-12 col-sm-12 error-container">
            <div className="error-text">
              <p> {account?.value} is linked to an IMY Structure.<br />
                Any new form for this account must be uploaded from the
                <Link to={ `/whd-statement/${ account?.parentAccountId ?? account?.id }` }> IMY Structure page.</Link></p>
            </div>
          </div>
        </div>}
      {cloneCollectableSupportingDocs === false
        && <div className="form-group col-12 col-sm-12">
          <div className="col-12 col-sm-12 error-container">
            <div className="error-text">
              <p id="affidavit-clone-error-msg">The Legal Entity for this Account does not collect Affidavits of Unchanged Status. The Supporting Document will not be cloned to this Account.</p>
            </div>
          </div>
        </div>}
      <div className="form-group columns">
        <div className="col-1" />
        <div className="col-6">
          <button
            id="clone-submit-btn"
            type="submit"
            className="btn btn-primary"
            disabled={ isCloningForm || (!formId || !customer || !account || accountPartOfAnImyStructure) }
          >
            { isCloningForm && <span className="icon loading mr" /> }
            Clone Form
          </button>
        </div>
      </div>
    </form>
  </div>);
};

CloneForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  introText: PropTypes.string.isRequired,
  sourceAccountId: PropTypes.number,
  forms: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  accounts: PropTypes.arrayOf(PropTypes.shape()),
  isCloningForm: PropTypes.bool.isRequired,
  cloneFormComplete: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func,
  customerSupportingDocs: PropTypes.arrayOf(PropTypes.shape())
};

CloneForm.defaultProps = {
  sourceAccountId: null,
  accounts: null,
  onSuccess: null,
  customerSupportingDocs: null
};

const CloneReduxForm = reduxForm({
  form: 'clone-form'
})(CloneForm);

const mapDispatchToProps = dispatch => ({ dispatch });

function mapStateToProps(state) {
  const {
    forms: {
      isCloningForm,
      cloneFormComplete
    },
    customer: {
      customerSupportingDocs
    }
  } = state;

  return {
    isCloningForm,
    cloneFormComplete,
    customerSupportingDocs
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CloneReduxForm);
